import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import '../../styles/ProfileDropdown.css';

// Function to generate default avatar URL based on user name
const getDefaultAvatar = (name) => {
  const encodedName = encodeURIComponent(name || 'User');
  return `https://ui-avatars.com/api/?name=${encodedName}&background=ff4d4d&color=fff&size=128`;
};

const ProfileDropdown = ({ user, onLogoutClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayName, setDisplayName] = useState(user?.displayName || user?.email?.split('@')[0] || 'User');
  const [avatar, setAvatar] = useState(user?.photoURL || getDefaultAvatar(displayName));
  const dropdownRef = useRef(null);
  
  // Fetch user profile to get display name
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      
      try {
        const userDocRef = doc(db, 'userProfiles', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists() && userDoc.data().displayName) {
          setDisplayName(userDoc.data().displayName);
          // Update avatar if it's the default
          if (!user.photoURL) {
            setAvatar(getDefaultAvatar(userDoc.data().displayName));
          }
        } else if (user.displayName) {
          // If no profile exists but user has a displayName in Auth
          setDisplayName(user.displayName);
          
          // Create a default profile (Phase 2 schema)
          const defaultProfile = {
            displayName: user.displayName,
            email: user.email,
            role: 'community',
            roleVerified: false,
            casesCreated: 0,
            evidenceSubmitted: 0,
            ordersCreated: 0,
        jobsCompleted: 0,
            totalSpent: 0,
            joinDate: serverTimestamp(),
            bio: '',
            location: '',
            budgetLimits: {
              daily: 10.00,
              monthly: 100.00,
              perAnalysis: 5.00
            },
            collaborationSettings: {
              allowDirectMessages: false,
              shareAnalysisHistory: false,
              expertConsultationAvailable: false
            }
          };
          
          try {
            // Use set with merge to create if not exists
            await setDoc(userDocRef, defaultProfile, { merge: true });
          } catch (error) {
            console.error('Error creating default profile:', error);
          }
        } else {
          // If no profile or displayName, use email username
          const fallbackName = user.email?.split('@')[0] || 'User';
          setDisplayName(fallbackName);
          if (!user.photoURL) {
            setAvatar(getDefaultAvatar(fallbackName));
          }
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        
        // Fallback to Auth data
        const fallbackName = user.displayName || user.email?.split('@')[0] || 'User';
        setDisplayName(fallbackName);
        if (!user.photoURL) {
          setAvatar(getDefaultAvatar(fallbackName));
        }
      }
    };
    
    fetchUserProfile();
  }, [user]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    // Use both mousedown and touchstart for mobile compatibility
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);
  
  if (!user) return null;
  
  return (
    <div className="profile-dropdown" ref={dropdownRef}>
      <div className="profile-button" onClick={() => setIsOpen(!isOpen)}>
        <img src={avatar} alt="Profile" className="profile-avatar" />
        <span className="profile-name">{displayName}</span>
      </div>
      
      {isOpen && (
        <div className="dropdown-menu">
          <div className="dropdown-header">
            <img src={avatar} alt="Profile" className="dropdown-avatar" />
            <div className="dropdown-user-info">
              <span className="dropdown-name">{displayName}</span>
              <span className="dropdown-email">{user.email}</span>
            </div>
          </div>
          
          <div className="dropdown-divider"></div>
          
          <div className="dropdown-items">
            <Link to="/profile" className="dropdown-item" onClick={() => setIsOpen(false)}>
              View Profile
            </Link>
            <Link to="/dashboard" className="dropdown-item" onClick={() => setIsOpen(false)}>
              Dashboard
            </Link>
            <Link to="/settings" className="dropdown-item" onClick={() => setIsOpen(false)}>
              Settings
            </Link>
            <Link to="/ai-dashboard" className="dropdown-item" onClick={() => setIsOpen(false)}>
              🤖 AI Dashboard
            </Link>
            <button 
              className="dropdown-item logout-button"
              onClick={() => {
                onLogoutClick();
                setIsOpen(false);
              }}
            >
              Log Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown; 